import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import {
   useGetRoleDetailQuery,
   useEditRoleMutation,
   useGetModuleListQuery,
   useGetPermissionListQuery,
} from '../../Redux/Service/RoleApi';
import { RolePermissionForm } from './Form';
import * as Yup from 'yup';

export default function EditRole() {
   const { id } = useParams();

   const { data: permissionsList, isLoading: isPermissionsLoading } = useGetPermissionListQuery();

   const { data: modulesList, isLoading: isModulesLoading } = useGetModuleListQuery();

   const {
      data: roleDetails,
      isLoading: isRoleLoading,
      isError: isRoleError,
   } = useGetRoleDetailQuery(id, { refetchOnFocus: true, refetchOnMountOrArgChange: true });

   const [updateRole] = useEditRoleMutation();
   const navigate = useNavigate();

   const validationSchema = Yup.object().shape({
      roleName: Yup.string()
         .required('Role name is required')
         .min(3, 'Role name must be at least 3 characters')
         .max(50, 'Role name must be at most 50 characters'),

      modules: Yup.array()
         .of(
            Yup.object().shape({
               moduleId: Yup.number().required('Module is required'),
               permissionIds: Yup.array()
                  .of(Yup.number().required())
                  .min(1, 'At least one permission is required for modules')
                  .required('At least one permission is required for modules'),
            }),
         )
         .min(1, 'At least one module is required')
         .optional(),
   });

   const handleSubmit = async (values, formikProps) => {
      try {
         await updateRole({ roleId: id, ...values }).unwrap();
         toast.success('Role updated successfully');
         navigate('/role-permissions');
      } catch (err) {
         console.log(err);
         toast.error(err?.data?.message || 'Failed to update role');
      } finally {
         formikProps.setSubmitting(false);
      }
   };

   const modulePermissions = modulesList?.response?.data?.map((module) => {
      if (module.name === 'dashboard')
         return {
            moduleId: module.id,
            moduleName: module.name,
            permissions: permissionsList?.response?.data.filter((per) => per.name === 'read'),
         };

      return {
         moduleId: module.id,
         moduleName: module.name,
         permissions: permissionsList?.response?.data,
      };
   });

   // Map the role detail API response into initialValues shape
   const buildInitialValues = () => {
      const role = roleDetails?.response?.data;

      if (!role) return null;

      return {
         roleName: role.roleName,
         modules: role.modules.map((module) => ({
            moduleId: module.moduleId,
            permissionIds: module.permission.map((p) => p.id),
         })),
      };
   };

   const initialValues = buildInitialValues();

   if (isRoleLoading || isModulesLoading || isPermissionsLoading) {
      return <div>Loading...</div>;
   }

   if (isRoleError || !initialValues) {
      return <div>Failed to load role details.</div>;
   }

   return (
      <RolePermissionForm
         initialValues={initialValues}
         handleSubmit={handleSubmit}
         validationSchema={validationSchema}
         showData={modulePermissions}
         isEdit={true}
      />
   );
}
